import React from 'react'
import { useAtomValue } from 'jotai'
import styled from 'styled-components'
import dt from 'dayjs'
import * as state from '../../states'
import { ERoleAccess, IOfferSpend } from '../../types'
import {
	UiTable,
	IUiTableConfig,
	IconDeleteBin,
	IconEdit,
	headCellFormatterSortable,
	ESortOrder,
	HistoryIconBtn,
	NotesIconBtn
} from '../ui'
import { OFFER_SPEND_PAGE_SORT_FIELDS, IOfferSpendSortField } from '../../hooks'
import { Access } from '../../lib'
import * as helpers from '../../helpers'
import { offerSpendConditions } from './helpers'
import { ALL_LANG_KEY } from '../../types/lang'
import { useTranslation } from 'react-i18next'

type IOfferSpendListTableProps = {
	items: IOfferSpend[];
	skip: number;
	sortBy: IOfferSpendSortField;
	setSortBy: (sortBy: IOfferSpendSortField) => void;
	order: ESortOrder;
	setOrder: (order: ESortOrder) => void;
	onEdit?: (item: IOfferSpend, e: React.MouseEvent) => void;
	onEditNotes?: (item: IOfferSpend, e: React.MouseEvent) => void;
	onRemove?: (item: IOfferSpend, e: React.MouseEvent) => void;
	onViewHistory?: (item: IOfferSpend, e: React.MouseEvent) => void;
}

const RefIf = styled.span`
	margin-left: 8px;
	color: #999;
	font-size: .8rem;
	&::before {
		content: '(';
	}
	&::after {
		content: ')';
	}
`

export const OfferSpendListTable = (props: IOfferSpendListTableProps) => {
	const {
		items = [],
		skip = 0,
		sortBy,
		setSortBy,
		order,
		setOrder,
		onEdit,
		onEditNotes,
		onRemove,
		onViewHistory,
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const offerSpendListTableConfig: IUiTableConfig<IOfferSpend>[] = [
		{
			key: 'id',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_id),
		},
		{
			key: 'at',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_date),
			rowCellFormatter: ({rowData}): React.JSX.Element => {
				const at = rowData?.at ?? null
				return <>
					{dt.utc(at).isValid() ? dt.utc(at).format('L') : '-'}
				</>
			},
		},
		{
			key: 'amount',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_amount),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const formatValue = helpers.formatAmount(rowData.amount)

				return <>{helpers.thousandSeparator(formatValue)}</>
			},
		},
		{
			key: 'accountId',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_account),
			rowCellFormatter: ({rowData}): React.JSX.Element => {
				const accountId = rowData?.accountId ?? null
				const account = rowData?.account ?? null
				// const email = account?.email ?? null
				const auth = account?.auth ?? null
				const login = auth?.login ?? null
				return <>
					<strong>[id: {accountId}]</strong>
					&nbsp;
					{login ?? '?'}
				</>
			},
		},
		// {
		// 	key: 'account.auth.login',
		// 	title: 'Account',
		// 	rowCellFormatter: ({rowData}): React.JSX.Element => {
		// 		const account = rowData?.account ?? null
		// 		// const email = account?.email ?? null
		// 		const auth = account?.auth ?? null
		// 		const login = auth?.login ?? null
		// 		return <>{login ?? '?'}</>
		// 	},
		// 	isSortable: false,
		// },
		{
			key: 'account.extra.keitaroLogin',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_kt_group),
			rowCellFormatter: ({ rowData }) => {
				const extra = rowData.account?.extra ?? null
				const valueFormat: string = extra?.keitaroLogin ?? ''
				return valueFormat
			}
		},
		{
			key: 'offerId',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_offer),
			rowCellFormatter: ({rowData}): React.JSX.Element => {
				const offerId = rowData?.offerId ?? null
				const offer = rowData?.offer ?? null
				const name = offer?.name ?? null
				return <>
					<strong>[id: {offerId}]</strong>
					&nbsp;
					{name ?? '?'}
				</>
			},
		},
		// {
		// 	key: 'offer.name',
		// 	title: 'Offer',
		// 	rowCellFormatter: ({rowData}) => {
		// 		const offer = rowData?.offer ?? null
		// 		const name = offer?.name ?? null
		// 		return <>{name ?? '?'}</>
		// 	},
		// 	isSortable: false,
		// },
		{
			key: 'scopeId',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_scope),
			rowCellFormatter: ({rowData}) => {
				const offer = rowData?.offer ?? null
				const scope = offer?.scope ?? null
				const scopeId = scope?.id ?? null
				const name = scope?.name ?? null
				return scopeId && name ? <>
					{/* {scopeId ?? ''} */}
					<strong>[id: {scopeId}]</strong>
					&nbsp;
					{name}
				</> : ''
			},
		},
		// {
		// 	key: 'scope.name',
		// 	title: 'Scope',
		// 	rowCellFormatter: ({rowData}) => {
		// 		const offer = rowData?.offer ?? null
		// 		const scope = offer?.scope ?? null
		// 		const name = scope?.name ?? null
		// 		return <>{name ?? ''}</>
		// 	},
		// 	isSortable: false,
		// },
		{
			key: 'history.empty',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_history),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({value, rowData}) => {
				const val = rowData?.history

				return <>
					{
						onViewHistory && !!$access.$(ERoleAccess.OfferSpendList)
							? (
								<HistoryIconBtn value={val}
								                onClick={(e) => onViewHistory && onViewHistory(rowData, e)}
								/>
							)
							: null
					}
				</>
			},
		},
		{
			key: 'notes',
			title: t(ALL_LANG_KEY.offer_spend_list_table.column_notes),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({value, rowData}) => {
				const val = rowData?.notes

				return <>
					{
						onEditNotes && !!$access.$(ERoleAccess.OfferSpendList)
							? (
								<NotesIconBtn value={val}
								              onClick={(e) => onEditNotes && onEditNotes(rowData, e)}
								/>
							)
							: null
					}
				</>
			},
		},
		{
			key: 'ACTIONS',
			title: 'Actions',
			headCellFormatter: ({ configItem }) => {
				return <div>{''}</div>
			},
			rowCellFormatter: ({ rowData }) => {
				const at = dt.utc(rowData.at)
				const {
					today,
					threeDaysBeforeToday,
					hasOfferSpendAnyDay,
					isBetweenAvailableRange,
					disabled
				} = offerSpendConditions(at, $access)

				return <>
					<div
						style={{
							margin: '0 auto',
							textAlign: 'center'
						}}
					>
						{
							!!onEdit && !!$access.$(ERoleAccess.OfferSpendModify) && !disabled
								? (
									<button
										name="edit"
										className="icon border-0 rounded-circle text-center edit bg-success-transparent edit-item"
										onClick={(e) => onEdit && onEdit(rowData, e)}
									>
										<IconEdit/>
									</button>
								)
								: null
						}

						{
							!!onRemove && !!$access.$(ERoleAccess.OfferSpendRemove) && !disabled
								? (
									<button
										name="delete"
										className="icon border-0 rounded-circle text-center trash bg-danger-transparent delete-item"
										onClick={(e) => onRemove && onRemove(rowData, e)}
									>
										<IconDeleteBin/>
									</button>
								)
								: null
						}
					</div>
				</>
			}
		}
	]

	const offerSpendListTableConfigSortable: IUiTableConfig<IOfferSpend>[] = offerSpendListTableConfig.map(
		(configItem) => {
			const configItemIsSortable = OFFER_SPEND_PAGE_SORT_FIELDS.includes(configItem.key as IOfferSpendSortField)

			if (!configItemIsSortable) {
				return configItem
			} else {
				return {
					...configItem,
					isSortable: true,
					headCellFormatter: ({
						value,
						configItem,
						cellIndex
					}) => {

						return headCellFormatterSortable({
							value,
							configItem,
							cellIndex,
							sortBy,
							setSortBy,
							order,
							setOrder
						})
					}
				}
			}
		})

	//console.log('OfferSpendListTable:: ', { pendListTableConfig, roleListTableConfigSortable })

	return <>
		<UiTable
			data={items}
			config={offerSpendListTableConfigSortable}
			// TODO: uncomment if necessary
			// classesToAdd={{
			// 	wrapper: 'table-sticky-wrapper',
			// }}
		/>
	</>
}